import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'

import lang from '@/lang'
import validateInputs from '@/modules/onboardingV2/helpers/meinbuero/validateInputs'
import { TextInput } from '@/modules/base/components/inputs'
import checkValidPhoneNumber from '@/modules/onboarding/components/_helpers/checkValidPhoneNumber'
import { setCompanyData } from '@/redux/ducks/onboarding/main-v2'
import { isMeinBuero } from '@/utils/build-env'
import InputNames from '@/modules/onboardingV2/constants/input-names'
import HubspotSelectInput from '@/modules/hubspot/hubspot-select-input'
import { HubspotService } from '@/modules/hubspot/hubspotService'
import { HUBSPOT_OPTIONS } from '@/modules/base/util/constants'
import Search from '@/modules/onboardingV2/components/search/search'
import { fetchCompanyData } from '@/redux/ducks/settings/company'

const { inputs } = lang.onboardingV2.user
const { crmObjects: { CONTACTS }, propertys: { CONTACT_SOURCES } } = HUBSPOT_OPTIONS

const isMeinbuero = isMeinBuero()

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  },
  column: {
    width: '100%',
  },
  multiRow: {
    width: '100%',
    '& + $name': {
      marginTop: 20,
      '@media (min-width: 800px)': {
        marginTop: 0,
      },
    },
    '@media (min-width: 800px)': {
      width: 'calc(50% - 10px)',
    },
  },
  inputsRow: {
    marginBottom: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 20,

    '& > div': {
      flex: 1,
    },

    [theme.devices.mobile]: {
      flexDirection: 'column',
    },
  },
}))

const UserInputs = props => {
  const { setInputsValid, setPhoneValid, isTaxConsultant = false } = props
  const classes = useStyles()
  const dispatch = useDispatch()
  const collaborationId = useSelector(state => state.user.info.collaborationId)
  const tenantCompanyData = useSelector(state => state.settings.company)
  useEffect(() => {
    dispatch(fetchCompanyData(collaborationId))
  }, [collaborationId])

  useEffect(() => {
    if (tenantCompanyData) {
      dispatch(setCompanyData(tenantCompanyData))
    }
  }, [tenantCompanyData])

  const getContactSources = async() => {
    return HubspotService.getProperty(CONTACTS, CONTACT_SOURCES)
  }
  const companyData = useSelector(state => state.onboarding.mainV2.companyData)
  const [errorMessages, setErrorMessages] = useState({ companyName: '', lastName: '', phone1: '' })
  const [industry, setIndustry] = useState(null)

  const validateInput = (name, value) => {
    if (!isMeinbuero) { return }

    const updatedErrorMessage = { ...errorMessages }
    updatedErrorMessage[name] = validateInputs(name, value)

    setErrorMessages(updatedErrorMessage)
  }

  useEffect(() => {
    const {
      firstName,
      lastName,
      phone1,
      companyName,
    } = companyData

    if (isMeinbuero) {
      const companyNameValid = companyName && !errorMessages.companyName
      const nameValid = firstName && lastName && !errorMessages.lastName
      const phoneValid = phone1 && !errorMessages.phone1
      setInputsValid(companyNameValid && nameValid && phoneValid && industry)
      setPhoneValid(phoneValid)
      return
    }

    setInputsValid(firstName && lastName)
    if (phone1) { setPhoneValid(checkValidPhoneNumber(phone1)) } else { setPhoneValid(true) }

  }, [companyData, industry])

  const handleInputChange = name => value => {
    validateInput(name, value)
    dispatch(setCompanyData({ ...companyData, [name]: value }))
  }
  const handleDropdownChange = name => option => dispatch(setCompanyData({ ...companyData, [name]: option.value }))

  return (
    <>
      <div className={classes.root}>
        <div className={classes.column}>
          <div className={classes.inputsRow}>
            <TextInput
              name={InputNames.companyName}
              label={inputs.companyName}
              value={companyData.companyName}
              errorMessage={errorMessages.companyName}
              placeholder={inputs.companyNamePlaceholder}
              onChange={handleInputChange(InputNames.companyName)}
              labelTooltip={isMeinbuero && { description: lang.onboardingV2.user.companyNameTooltip, placement: 'top', showInfoIcon: true }}
            />
          </div>
        </div>
      </div>
      <div className={classes.root}>
        <div className={classes.column}>
          <div className={classes.inputsRow}>
            <TextInput
              name={InputNames.firstName}
              className={classes.multiRow}
              label={inputs.firstName}
              value={companyData.firstName}
              placeholder={inputs.firstNamePlaceholder}
              onChange={handleInputChange(InputNames.firstName)}
            />
            <TextInput
              name={InputNames.lastName}
              className={classes.multiRow}
              label={inputs.lastName}
              value={companyData.lastName}
              placeholder={inputs.lastNamePlaceholder}
              errorMessage={errorMessages.lastName}
              onChange={handleInputChange(InputNames.lastName)}
            />
          </div>
        </div>
      </div>
      <div className={classes.root}>
        <div className={classes.column}>
          <div className={classes.inputsRow}>
            <TextInput
              name={InputNames.phone1}
              className={classes.multiRow}
              label={inputs.phone}
              value={companyData.phone1}
              placeholder={inputs.phonePlaceholder}
              errorMessage={errorMessages.phone1}
              onChange={handleInputChange(InputNames.phone1)}
            />
            {isMeinbuero
              ? <Search setInputsValid={setInputsValid} onChange={setIndustry} useFormTheme={!isTaxConsultant} />
              : <HubspotSelectInput
                options={getContactSources}
                title={lang.onboardingV2.user.inputs.source}
                value={companyData?.source}
                className={classes.multiRow}
                onChange={handleDropdownChange(InputNames.source)}
              />}
          </div>
        </div>
      </div>
    </>
  )
}

UserInputs.propTypes = {
  setInputsValid: PropTypes.func,
  setPhoneValid: PropTypes.func,
}

UserInputs.defaultProps = {
  setInputsValid: () => { },
  setPhoneValid: () => { },
}

export default UserInputs
